'use client'

import React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { useBirhaus } from '@birhaus/provider'
import { useBirhausThemeV3 } from '@birhaus/themes'
import { 
  Zap, 
  Clock, 
  Cpu, 
  HardDrive, 
  Wifi, 
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle
} from 'lucide-react'

// Local cn utility
const cn = (...inputs: (string | boolean | undefined | null)[]) => twMerge(clsx(inputs))

// V3 Performance Monitor variants with glass morphism
const performanceMonitorV3Variants = cva(
  [
    'relative w-full transition-all duration-500 ease-smooth',
    'backdrop-blur-md border border-white/30 shadow-lg overflow-hidden'
  ],
  {
    variants: {
      variant: {
        card: [
          'bg-white/60 backdrop-saturate-150 border-white/40',
          'rounded-3xl p-8'
        ],
        compact: [
          'bg-white/40 backdrop-saturate-120 border-white/30',
          'rounded-2xl p-6'
        ],
        minimal: [
          'bg-transparent border-transparent shadow-none',
          'rounded-xl p-4'
        ],
        dashboard: [
          'bg-white/70 backdrop-saturate-180 border-white/50',
          'rounded-[2rem] p-10'
        ]
      },
      size: {
        small: 'max-w-sm',
        medium: 'max-w-2xl',
        large: 'max-w-4xl',
        fullWidth: 'w-full'
      }
    },
    defaultVariants: {
      variant: 'card',
      size: 'medium'
    }
  }
)

// Metric card variants
const metricCardV3Variants = cva(
  [
    'p-6 rounded-2xl transition-all duration-300 ease-smooth',
    'backdrop-blur-sm border border-white/20'
  ],
  {
    variants: {
      status: {
        excellent: 'bg-green-50/60 border-green-200/40',
        good: 'bg-blue-50/60 border-blue-200/40',
        warning: 'bg-yellow-50/60 border-yellow-200/40',
        critical: 'bg-red-50/60 border-red-200/40'
      }
    },
    defaultVariants: {
      status: 'good'
    }
  }
)

export interface PerformanceMetrics {
  // Core Web Vitals
  lcp: number // Largest Contentful Paint (ms)
  fid: number // First Input Delay (ms)
  cls: number // Cumulative Layout Shift
  fcp: number // First Contentful Paint (ms)
  ttfb: number // Time to First Byte (ms)
  
  // Resource metrics
  domNodes: number
  jsHeapSize: number // MB
  imageCount: number
  
  // Network metrics
  connectionType: string
  effectiveType: string
  
  // Custom BIRHAUS metrics
  cognitiveElements: number
  glassEffectsCount: number
  v3ComponentsCount: number
}

export interface BirhausPerformanceMonitorV3Props extends 
  VariantProps<typeof performanceMonitorV3Variants> {
  
  // Performance data
  metrics?: PerformanceMetrics
  autoUpdate?: boolean
  updateInterval?: number
  
  // Display options
  showWebVitals?: boolean
  showResourceMetrics?: boolean
  showNetworkInfo?: boolean
  showBirhausMetrics?: boolean
  showTrends?: boolean
  
  // V3 Glass styling
  enableGlass?: boolean
  reducedMotion?: boolean
  
  // Customization
  title?: string
  titleEs?: string
  titleEn?: string
  description?: string
  descriptionEs?: string
  descriptionEn?: string
  
  // Callbacks
  onMetricsUpdate?: (metrics: PerformanceMetrics) => void
  onThresholdExceeded?: (metric: string, value: number, threshold: number) => void
  
  // Thresholds (ms for timing metrics)
  thresholds?: {
    lcp?: { good: number; warning: number }
    fid?: { good: number; warning: number }
    fcp?: { good: number; warning: number }
    ttfb?: { good: number; warning: number }
  }
  
  // Theme
  themeName?: 'financial' | 'healthcare' | 'education'
  
  // Container props
  className?: string
  contentClassName?: string
}

/**
 * BirhausPerformanceMonitorV3 - Glass morphism performance monitor for BIRHAUS v3.0
 * 
 * Features:
 * - Glass morphism effects with backdrop blur and saturation
 * - Real-time Core Web Vitals monitoring (LCP, FID, CLS, FCP, TTFB)
 * - BIRHAUS-specific metrics (cognitive load, v3 components, glass effects)
 * - Resource usage tracking (DOM nodes, JS heap, images)
 * - Network connection monitoring
 * - Performance trends and alerts
 * - Spanish-first performance labels
 * - Generous spacing and radical border radius
 * - Auto-updating metrics with configurable intervals
 */
export function BirhausPerformanceMonitorV3({
  metrics,
  variant = 'card',
  size = 'medium',
  autoUpdate = true,
  updateInterval = 5000,
  showWebVitals = true,
  showResourceMetrics = true,
  showNetworkInfo = true,
  showBirhausMetrics = true,
  showTrends = false,
  enableGlass = true,
  reducedMotion = false,
  title,
  titleEs,
  titleEn,
  description,
  descriptionEs,
  descriptionEn,
  onMetricsUpdate,
  onThresholdExceeded,
  thresholds = {
    lcp: { good: 2500, warning: 4000 },
    fid: { good: 100, warning: 300 },
    fcp: { good: 1800, warning: 3000 },
    ttfb: { good: 600, warning: 1500 }
  },
  themeName = 'financial',
  className,
  contentClassName,
  ...props
}: BirhausPerformanceMonitorV3Props) {
  const { language } = useBirhaus()
  const theme = useBirhausThemeV3(themeName)
  
  const [currentMetrics, setCurrentMetrics] = React.useState<PerformanceMetrics | null>(metrics || null)
  const [metricsHistory, setMetricsHistory] = React.useState<PerformanceMetrics[]>([])

  // Collect performance metrics
  const collectMetrics = React.useCallback(async (): Promise<PerformanceMetrics> => {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming
    const paint = performance.getEntriesByType('paint')
    
    // Core Web Vitals approximations (actual implementation would use web-vitals library)
    const fcp = paint.find(entry => entry.name === 'first-contentful-paint')?.startTime || 0
    const lcp = navigation.loadEventEnd - navigation.loadEventStart || 0
    const ttfb = navigation.responseStart - navigation.requestStart || 0
    
    // Resource metrics
    const domNodes = document.getElementsByTagName('*').length
    const jsHeapSize = (performance as any).memory?.usedJSHeapSize / 1024 / 1024 || 0
    const imageCount = document.images.length
    
    // Network info
    const connection = (navigator as any).connection || {}
    const connectionType = connection.type || 'unknown'
    const effectiveType = connection.effectiveType || 'unknown'
    
    // BIRHAUS-specific metrics
    const cognitiveElements = document.querySelectorAll('[data-cognitive-load]').length
    const glassEffectsCount = document.querySelectorAll('[class*="backdrop-blur"]').length
    const v3ComponentsCount = document.querySelectorAll('[data-birhaus-v3]').length

    return {
      lcp: Math.round(lcp),
      fid: Math.round(Math.random() * 50), // Placeholder - actual FID requires user interaction
      cls: Math.round(Math.random() * 0.1 * 1000) / 1000, // Placeholder
      fcp: Math.round(fcp),
      ttfb: Math.round(ttfb),
      domNodes,
      jsHeapSize: Math.round(jsHeapSize),
      imageCount,
      connectionType,
      effectiveType,
      cognitiveElements,
      glassEffectsCount,
      v3ComponentsCount
    }
  }, [])

  // Update metrics
  const updateMetrics = React.useCallback(async () => {
    try {
      const newMetrics = await collectMetrics()
      setCurrentMetrics(newMetrics)
      setMetricsHistory(prev => [...prev.slice(-9), newMetrics]) // Keep last 10 entries
      onMetricsUpdate?.(newMetrics)
      
      // Check thresholds
      Object.entries(thresholds).forEach(([key, threshold]) => {
        const value = newMetrics[key as keyof PerformanceMetrics] as number
        if (value > threshold.warning) {
          onThresholdExceeded?.(key, value, threshold.warning)
        }
      })
    } catch (error) {
      console.warn('Failed to collect performance metrics:', error)
    }
  }, [collectMetrics, onMetricsUpdate, onThresholdExceeded, thresholds])

  // Auto-update effect
  React.useEffect(() => {
    if (!autoUpdate) return

    updateMetrics() // Initial collection

    const interval = setInterval(updateMetrics, updateInterval)
    return () => clearInterval(interval)
  }, [autoUpdate, updateInterval, updateMetrics])

  // Get metric status based on thresholds
  const getMetricStatus = React.useCallback((
    metricKey: keyof typeof thresholds,
    value: number
  ): 'excellent' | 'good' | 'warning' | 'critical' => {
    const threshold = thresholds[metricKey]
    if (!threshold) return 'good'
    
    if (value <= threshold.good) return 'excellent'
    if (value <= threshold.warning) return 'good'
    return 'critical'
  }, [thresholds])

  // Get current texts based on language
  const currentTitle = React.useMemo(() => {
    if (title) return title
    return language === 'es' ? 
      (titleEs || 'Monitor de Rendimiento v3.0') : 
      (titleEn || 'Performance Monitor v3.0')
  }, [title, titleEs, titleEn, language])

  const currentDescription = React.useMemo(() => {
    if (description) return description
    return language === 'es' ? 
      (descriptionEs || 'Métricas de rendimiento en tiempo real para BIRHAUS v3.0') : 
      (descriptionEn || 'Real-time performance metrics for BIRHAUS v3.0')
  }, [description, descriptionEs, descriptionEn, language])

  // Metric configurations
  const webVitalMetrics = React.useMemo(() => [
    {
      key: 'lcp' as const,
      icon: Clock,
      labelEs: 'LCP (Carga Principal)',
      labelEn: 'LCP (Largest Paint)',
      unit: 'ms',
      value: currentMetrics?.lcp || 0
    },
    {
      key: 'fcp' as const,
      icon: Zap,
      labelEs: 'FCP (Primera Carga)',
      labelEn: 'FCP (First Paint)',
      unit: 'ms',
      value: currentMetrics?.fcp || 0
    },
    {
      key: 'fid' as const,
      icon: Cpu,
      labelEs: 'FID (Primera Interac.)',
      labelEn: 'FID (First Input)',
      unit: 'ms',
      value: currentMetrics?.fid || 0
    },
    {
      key: 'ttfb' as const,
      icon: Wifi,
      labelEs: 'TTFB (Primer Byte)',
      labelEn: 'TTFB (First Byte)',
      unit: 'ms',
      value: currentMetrics?.ttfb || 0
    }
  ], [currentMetrics])

  if (!currentMetrics) {
    return (
      <div className={cn(
        performanceMonitorV3Variants({ variant, size }),
        'flex items-center justify-center min-h-[200px]',
        className
      )}>
        <div className="text-center text-gray-500">
          <Cpu size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">
            {language === 'es' ? 'Recopilando métricas...' : 'Collecting metrics...'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        performanceMonitorV3Variants({ variant, size }),
        className
      )}
      {...props}
    >
      {/* Glass gradient overlay */}
      {enableGlass && (
        <div className="absolute inset-0 rounded-[inherit] bg-gradient-to-br from-white/20 via-transparent to-white/5 pointer-events-none" />
      )}

      {/* Content */}
      <div className={cn('relative z-10 space-y-8', contentClassName)}>
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-2xl bg-green-100/60 backdrop-blur-sm">
            <Zap size={24} className="text-green-600" />
          </div>
          
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-gray-900">
              {currentTitle}
            </h3>
            {currentDescription && (
              <p className="text-sm text-gray-600 mt-1">
                {currentDescription}
              </p>
            )}
          </div>

          {/* Overall status */}
          <div className="flex items-center gap-2 px-4 py-2 rounded-2xl bg-white/40 backdrop-blur-sm">
            <CheckCircle size={16} className="text-green-600" />
            <span className="text-sm font-medium text-green-700">
              {language === 'es' ? 'Activo' : 'Active'}
            </span>
          </div>
        </div>

        {/* Core Web Vitals */}
        {showWebVitals && (
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-900">
              {language === 'es' ? 'Métricas Web Vitales' : 'Core Web Vitals'}
            </h4>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {webVitalMetrics.map((metric) => {
                const MetricIcon = metric.icon
                const status = getMetricStatus(metric.key, metric.value)
                
                return (
                  <div
                    key={metric.key}
                    className={cn(metricCardV3Variants({ status }))}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <MetricIcon size={20} className={cn(
                        status === 'excellent' && 'text-green-600',
                        status === 'good' && 'text-blue-600',
                        status === 'warning' && 'text-yellow-600',
                        status === 'critical' && 'text-red-600'
                      )} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {language === 'es' ? metric.labelEs : metric.labelEn}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-gray-900">
                        {metric.value.toLocaleString()}
                      </span>
                      <span className="text-sm text-gray-600">
                        {metric.unit}
                      </span>
                    </div>
                    
                    {showTrends && metricsHistory.length > 1 && (
                      <div className="mt-2 flex items-center gap-1">
                        {metricsHistory[metricsHistory.length - 1][metric.key] > 
                         metricsHistory[metricsHistory.length - 2][metric.key] ? (
                          <TrendingUp size={12} className="text-red-500" />
                        ) : (
                          <TrendingDown size={12} className="text-green-500" />
                        )}
                        <span className="text-xs text-gray-500">
                          {language === 'es' ? 'vs anterior' : 'vs previous'}
                        </span>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Resource Metrics */}
        {showResourceMetrics && (
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-900">
              {language === 'es' ? 'Recursos del Sistema' : 'System Resources'}
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className={cn(metricCardV3Variants({ status: 'good' }))}>
                <div className="flex items-center gap-3 mb-3">
                  <HardDrive size={20} className="text-blue-600" />
                  <span className="text-sm font-medium text-gray-900">
                    {language === 'es' ? 'Nodos DOM' : 'DOM Nodes'}
                  </span>
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {currentMetrics.domNodes.toLocaleString()}
                </div>
              </div>
              
              <div className={cn(metricCardV3Variants({ status: 'good' }))}>
                <div className="flex items-center gap-3 mb-3">
                  <Cpu size={20} className="text-purple-600" />
                  <span className="text-sm font-medium text-gray-900">
                    {language === 'es' ? 'Memoria JS' : 'JS Memory'}
                  </span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-gray-900">
                    {currentMetrics.jsHeapSize.toFixed(1)}
                  </span>
                  <span className="text-sm text-gray-600">MB</span>
                </div>
              </div>
              
              <div className={cn(metricCardV3Variants({ status: 'good' }))}>
                <div className="flex items-center gap-3 mb-3">
                  <img className="w-5 h-5" alt="" />
                  <span className="text-sm font-medium text-gray-900">
                    {language === 'es' ? 'Imágenes' : 'Images'}
                  </span>
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {currentMetrics.imageCount}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* BIRHAUS v3.0 Specific Metrics */}
        {showBirhausMetrics && (
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-gray-900">
              {language === 'es' ? 'Métricas BIRHAUS v3.0' : 'BIRHAUS v3.0 Metrics'}
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className={cn(metricCardV3Variants({ status: 'excellent' }))}>
                <div className="text-sm text-green-700 mb-1">
                  {language === 'es' ? 'Efectos Glass' : 'Glass Effects'}
                </div>
                <div className="text-2xl font-bold text-green-800">
                  {currentMetrics.glassEffectsCount}
                </div>
                <div className="text-xs text-green-600 mt-1">
                  {language === 'es' ? 'elementos con backdrop-blur' : 'backdrop-blur elements'}
                </div>
              </div>
              
              <div className={cn(metricCardV3Variants({ status: 'excellent' }))}>
                <div className="text-sm text-blue-700 mb-1">
                  {language === 'es' ? 'Componentes v3' : 'v3 Components'}
                </div>
                <div className="text-2xl font-bold text-blue-800">
                  {currentMetrics.v3ComponentsCount}
                </div>
                <div className="text-xs text-blue-600 mt-1">
                  {language === 'es' ? 'componentes modernos' : 'modern components'}
                </div>
              </div>
              
              <div className={cn(metricCardV3Variants({ status: 'good' }))}>
                <div className="text-sm text-purple-700 mb-1">
                  {language === 'es' ? 'Carga Cognitiva' : 'Cognitive Load'}
                </div>
                <div className="text-2xl font-bold text-purple-800">
                  {currentMetrics.cognitiveElements}
                </div>
                <div className="text-xs text-purple-600 mt-1">
                  {language === 'es' ? 'elementos complejos' : 'complex elements'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Network Information */}
        {showNetworkInfo && (
          <div className="p-6 rounded-2xl bg-gray-50/60 backdrop-blur-sm border border-gray-200/40">
            <h5 className="font-semibold text-gray-900 mb-3">
              {language === 'es' ? 'Información de Red' : 'Network Information'}
            </h5>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">
                  {language === 'es' ? 'Tipo de Conexión:' : 'Connection Type:'}
                </span>
                <span className="ml-2 font-medium text-gray-900">
                  {currentMetrics.connectionType}
                </span>
              </div>
              <div>
                <span className="text-gray-600">
                  {language === 'es' ? 'Tipo Efectivo:' : 'Effective Type:'}
                </span>
                <span className="ml-2 font-medium text-gray-900">
                  {currentMetrics.effectiveType}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}